import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Chip,
  ListItemText,
  Checkbox,
  OutlinedInput,
  SelectChangeEvent,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAlert } from "../../context/AlertContext";
import {
  Evaluation,
  EvaluationRequest,
  Prompt,
  Test,
  JsonSchema,
  Provider,
} from "../../types";
import api from "../../services/api";

interface EvaluationFormProps {
  initialData?: Evaluation;
  isEdit?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const EvaluationForm: React.FC<EvaluationFormProps> = ({
  initialData,
  isEdit = false,
}) => {
  const navigate = useNavigate();
  const alert = useAlert();
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchingData, setFetchingData] = useState<boolean>(true);

  // Data for selection
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [tests, setTests] = useState<Test[]>([]);
  const [schemas, setSchemas] = useState<JsonSchema[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);

  // Define validation schema
  const validationSchema = Yup.object({
    promptIds: Yup.array()
      .of(Yup.string())
      .min(1, "At least one prompt is required"),
    testIds: Yup.array()
      .of(Yup.string())
      .min(1, "At least one test is required"),
    providerIds: Yup.array()
      .of(Yup.string())
      .min(1, "At least one provider is required"),
    schemaId: Yup.string(),
  });

  // Function to extract IDs from possibly populated objects
  const extractIds = (items: any[] | undefined): string[] => {
    if (!items || items.length === 0) return [];

    // If the first item has an _id property, it's a populated object
    if (typeof items[0] === "object" && items[0]._id) {
      return items.map((item) => item._id);
    }

    // Otherwise, assume it's already an array of strings
    return items as string[];
  };

  // Initialize formik
  const formik = useFormik<EvaluationRequest>({
    initialValues: {
      promptIds: extractIds(initialData?.promptIds || initialData?.prompts),
      testIds: extractIds(initialData?.testIds || initialData?.tests),
      providerIds: extractIds(
        initialData?.providerIds || initialData?.providers,
      ),
      schemaId:
        (initialData?.schemaId &&
          (typeof initialData.schemaId === "string"
            ? initialData.schemaId
            : (initialData.schemaId as any)._id)) ||
        "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isEdit && initialData?._id) {
          // Update the existing evaluation
          const response = await api.evaluations.update(
            initialData._id,
            values,
          );
          alert.success("Evaluation updated successfully");
          navigate(`/evaluations/${response.data._id}`);
        } else {
          const response = await api.evaluations.create(values);
          alert.success("Evaluation created successfully");
          navigate(`/evaluations/${response.data._id}`);
        }
      } catch (error) {
        console.error("Error saving evaluation:", error);
        alert.error("Failed to save evaluation");
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch data for selection
  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true);
      try {
        const [
          promptsResponse,
          testsResponse,
          schemasResponse,
          providersResponse,
        ] = await Promise.all([
          api.prompts.getAll(),
          api.tests.getAll(),
          api.schemas.getAll(),
          api.providers.getAll(),
        ]);

        setPrompts(promptsResponse.data);
        setTests(testsResponse.data);
        setSchemas(schemasResponse.data);
        setProviders(providersResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        alert.error("Failed to fetch required data");
      } finally {
        setFetchingData(false);
      }
    };

    fetchData();
  }, [alert]);

  // Handle multi-select changes
  const handleMultiSelectChange = (
    event: SelectChangeEvent<string[]>,
    fieldName: string,
  ) => {
    const {
      target: { value },
    } = event;

    formik.setFieldValue(
      fieldName,
      typeof value === "string" ? value.split(",") : value,
    );
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Prompts
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormControl
          fullWidth
          error={formik.touched.promptIds && Boolean(formik.errors.promptIds)}
          disabled={fetchingData}
          sx={{ mb: 3 }}
        >
          <InputLabel id="prompts-label">Select Prompts</InputLabel>
          <Select
            labelId="prompts-label"
            multiple
            value={formik.values.promptIds}
            onChange={(e) => handleMultiSelectChange(e, "promptIds")}
            input={<OutlinedInput label="Select Prompts" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const prompt = prompts.find((p) => p._id === value);
                  return (
                    <Chip
                      key={value}
                      label={prompt?.name || value}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {prompts.map((prompt) => (
              <MenuItem key={prompt._id} value={prompt._id}>
                <Checkbox
                  checked={formik.values.promptIds.indexOf(prompt._id) > -1}
                />
                <ListItemText
                  primary={prompt.name}
                  secondary={`${prompt.type} - ${prompt.description || "No description"}`}
                />
              </MenuItem>
            ))}
          </Select>
          {formik.touched.promptIds && formik.errors.promptIds && (
            <FormHelperText>{formik.errors.promptIds as string}</FormHelperText>
          )}
        </FormControl>

        <Typography variant="h6" gutterBottom>
          Tests
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormControl
          fullWidth
          error={formik.touched.testIds && Boolean(formik.errors.testIds)}
          disabled={fetchingData}
          sx={{ mb: 3 }}
        >
          <InputLabel id="tests-label">Select Tests</InputLabel>
          <Select
            labelId="tests-label"
            multiple
            value={formik.values.testIds}
            onChange={(e) => handleMultiSelectChange(e, "testIds")}
            input={<OutlinedInput label="Select Tests" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const test = tests.find((t) => t._id === value);
                  return (
                    <Chip
                      key={value}
                      label={test?.name || value}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {tests.map((test) => (
              <MenuItem key={test._id} value={test._id}>
                <Checkbox
                  checked={formik.values.testIds.indexOf(test._id) > -1}
                />
                <ListItemText
                  primary={test.name}
                  secondary={test.description || "No description"}
                />
              </MenuItem>
            ))}
          </Select>
          {formik.touched.testIds && formik.errors.testIds && (
            <FormHelperText>{formik.errors.testIds as string}</FormHelperText>
          )}
        </FormControl>

        <Typography variant="h6" gutterBottom>
          Providers
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormControl
          fullWidth
          error={
            formik.touched.providerIds && Boolean(formik.errors.providerIds)
          }
          disabled={fetchingData}
          sx={{ mb: 3 }}
        >
          <InputLabel id="providers-label">Select Providers</InputLabel>
          <Select
            labelId="providers-label"
            multiple
            value={formik.values.providerIds}
            onChange={(e) => handleMultiSelectChange(e, "providerIds")}
            input={<OutlinedInput label="Select Providers" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const provider = providers.find((p) => p._id === value);
                  return (
                    <Chip
                      key={value}
                      label={provider?.name || value}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {providers.map((provider) => (
              <MenuItem key={provider._id} value={provider._id}>
                <Checkbox
                  checked={formik.values.providerIds.indexOf(provider._id) > -1}
                />
                <ListItemText
                  primary={provider.name}
                  secondary={provider.provider}
                />
              </MenuItem>
            ))}
          </Select>
          {formik.touched.providerIds && formik.errors.providerIds && (
            <FormHelperText>
              {formik.errors.providerIds as string}
            </FormHelperText>
          )}
        </FormControl>

        <Typography variant="h6" gutterBottom>
          JSON Schema (Optional)
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormControl
          fullWidth
          error={formik.touched.schemaId && Boolean(formik.errors.schemaId)}
          disabled={fetchingData}
        >
          <InputLabel id="schema-label">Select Schema (Optional)</InputLabel>
          <Select
            labelId="schema-label"
            value={formik.values.schemaId}
            onChange={formik.handleChange}
            name="schemaId"
            input={<OutlinedInput label="Select Schema (Optional)" />}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {schemas.map((schema) => (
              <MenuItem key={schema._id} value={schema._id}>
                {schema.name}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.schemaId && formik.errors.schemaId && (
            <FormHelperText>{formik.errors.schemaId}</FormHelperText>
          )}
        </FormControl>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => navigate("/evaluations")}
        >
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loading={loading}
          disabled={fetchingData}
        >
          {isEdit ? "Update Evaluation" : "Run Evaluation"}
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default EvaluationForm;
